import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import apiService from '../services/apiService';
import AuthService from '../services/auth.service';
import { can, PERMISSIONS } from '../config/permissions';
import '../styles/UsersView.css';

const ROLES = ['SUPERADMIN', 'ADMIN', 'DRIVER'];

const EMPTY_FORM = {
  nombre: '', apellido: '', telefono: '', email: '',
  password: '', role: 'DRIVER', estado: 'activo'
};

const roleClass = { SUPERADMIN: 'role-superadmin', ADMIN: 'role-admin', DRIVER: 'role-driver' };
const roleLabel = { SUPERADMIN: 'Super Admin', ADMIN: 'Admin', DRIVER: 'Driver' };

export default function UsersView() {
  const [users, setUsers]             = useState([]);
  const [loading, setLoading]         = useState(false);
  const [modal, setModal]             = useState(null); // 'create' | 'edit' | 'delete' | 'password'
  const [selected, setSelected]       = useState(null);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [newPassword, setNewPassword] = useState('');
  const [saving, setSaving]           = useState(false);

  const currentUser = AuthService.getCurrentUser();
  const userRole    = currentUser && currentUser.data && currentUser.data.role;

  // Helpers de permisos — un solo lugar para leer "qué puede hacer este usuario"
  const canCreateDriver  = can(userRole, PERMISSIONS.USERS_CREATE_DRIVER);
  const canCreateAny     = can(userRole, PERMISSIONS.USERS_CREATE_ANY);
  const canEdit          = can(userRole, PERMISSIONS.USERS_EDIT);
  const canDelete        = can(userRole, PERMISSIONS.USERS_DELETE);
  const canResetPassword = can(userRole, PERMISSIONS.USERS_RESET_PASSWORD);

  const creatableRoles = canCreateAny ? ROLES : ['DRIVER'];
  const editableRoles  = canCreateAny ? ROLES : ROLES.filter(r => r !== 'SUPERADMIN');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiService.getUsers();
      setUsers(data);
    } catch (err) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openCreate = () => { setForm(EMPTY_FORM); setModal('create'); };

  const openEdit = (user) => {
    setSelected(user);
    setForm({
      nombre: user.nombre, apellido: user.apellido,
      telefono: user.telefono || '', email: user.email,
      password: '', role: user.role, estado: user.estado
    });
    setModal('edit');
  };

  const openDelete   = (user) => { setSelected(user); setModal('delete'); };
  const openPassword = (user) => { setSelected(user); setNewPassword(''); setModal('password'); };
  const closeModal   = () => { setModal(null); setSelected(null); };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.telefono) delete payload.telefono;
      await apiService.createUser(payload);
      toast.success('Usuario creado correctamente');
      closeModal();
      loadUsers();
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.message;
      toast.error(msg || 'Error al crear usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { password, ...payload } = form;
      if (!payload.telefono) payload.telefono = null;
      await apiService.updateUser(selected.id, payload);
      toast.success('Usuario actualizado correctamente');
      closeModal();
      loadUsers();
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.message;
      toast.error(msg || 'Error al actualizar usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await apiService.deleteUser(selected.id);
      toast.success('Usuario eliminado correctamente');
      closeModal();
      loadUsers();
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.message;
      toast.error(msg || 'Error al eliminar usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { toast.error('La contraseña debe tener al menos 6 caracteres'); return; }
    setSaving(true);
    try {
      await apiService.resetUserPassword(selected.id, newPassword);
      toast.success('Contraseña actualizada correctamente');
      closeModal();
    } catch (err) {
      const msg = err.response && err.response.data && err.response.data.message;
      toast.error(msg || 'Error al resetear contraseña');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="users-view">
      <div className="users-header">
        <div className="users-title">
          <h2>Gestión de Usuarios</h2>
          <span className="users-count">{users.length} usuario{users.length !== 1 ? 's' : ''}</span>
        </div>
        {canCreateDriver && (
          <button className="btn btn-primary" onClick={openCreate}>+ Nuevo Usuario</button>
        )}
      </div>

      {loading ? (
        <div className="users-loading">Cargando usuarios...</div>
      ) : (
        <div className="users-table-wrapper">
          <table className="users-table">
            <thead>
              <tr>
                <th>Nombre</th><th>Email</th><th>Teléfono</th>
                <th>Rol</th><th>Estado</th><th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr><td colSpan="6" className="users-empty">No hay usuarios registrados</td></tr>
              ) : (
                users.map(user => (
                  <tr key={user.id}>
                    <td className="user-name-cell">
                      <div className="user-avatar">{user.nombre[0]}{user.apellido[0]}</div>
                      <span>{user.nombre} {user.apellido}</span>
                    </td>
                    <td>{user.email}</td>
                    <td>{user.telefono || <span className="text-muted">—</span>}</td>
                    <td>
                      <span className={`role-badge ${roleClass[user.role] || ''}`}>
                        {roleLabel[user.role] || user.role}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${user.estado === 'activo' ? 'status-active' : 'status-inactive'}`}>
                        {user.estado === 'activo' ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="user-actions">
                      {canEdit && (canCreateAny || user.role !== 'SUPERADMIN') && (
                        <button className="btn btn-sm btn-secondary" onClick={() => openEdit(user)}>Editar</button>
                      )}
                      {canResetPassword && (
                        <button className="btn btn-sm btn-warning" onClick={() => openPassword(user)}>Clave</button>
                      )}
                      {canDelete && (
                        <button className="btn btn-sm btn-danger" onClick={() => openDelete(user)}>Eliminar</button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal Crear / Editar */}
      {(modal === 'create' || modal === 'edit') && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modal === 'create' ? 'Nuevo Usuario' : 'Editar Usuario'}</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={modal === 'create' ? handleCreate : handleUpdate}>
              <div className="form-row">
                <div className="form-group">
                  <label>Nombre *</label>
                  <input name="nombre" value={form.nombre} onChange={handleFormChange} required />
                </div>
                <div className="form-group">
                  <label>Apellido *</label>
                  <input name="apellido" value={form.apellido} onChange={handleFormChange} required />
                </div>
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" name="email" value={form.email} onChange={handleFormChange} required />
              </div>
              <div className="form-group">
                <label>Teléfono</label>
                <input name="telefono" value={form.telefono} onChange={handleFormChange} placeholder="Opcional" />
              </div>
              {modal === 'create' && (
                <div className="form-group">
                  <label>Contraseña *</label>
                  <input type="password" name="password" value={form.password} onChange={handleFormChange} required minLength={6} />
                </div>
              )}
              <div className="form-row">
                <div className="form-group">
                  <label>Rol *</label>
                  <select
                    name="role"
                    value={form.role}
                    onChange={handleFormChange}
                    disabled={modal === 'edit' && !canCreateAny}
                  >
                    {(modal === 'create' ? creatableRoles : editableRoles).map(r => (
                      <option key={r} value={r}>{roleLabel[r]}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Estado *</label>
                  <select name="estado" value={form.estado} onChange={handleFormChange}>
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Guardando...' : modal === 'create' ? 'Crear Usuario' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Eliminar */}
      {modal === 'delete' && selected && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Eliminar Usuario</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <p>¿Estás seguro de que quieres eliminar a <strong>{selected.nombre} {selected.apellido}</strong>?</p>
              <p className="text-muted">Esta acción desactivará la cuenta.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
              <button type="button" className="btn btn-danger" onClick={handleDelete} disabled={saving}>
                {saving ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Resetear Contraseña */}
      {modal === 'password' && selected && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Resetear Contraseña</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleResetPassword}>
              <div className="modal-body">
                <p>Nueva contraseña para <strong>{selected.nombre} {selected.apellido}</strong></p>
                <div className="form-group">
                  <label>Nueva Contraseña *</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    placeholder="Mínimo 6 caracteres"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-warning" disabled={saving}>
                  {saving ? 'Guardando...' : 'Actualizar Contraseña'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
